import { forwardRef, type ComponentProps, type Ref } from "react"
import useSidebarContext from "@/v2-backoffice-client/components/sidebar/sidebar.context"
import SidebarItemContent from "@/v2-backoffice-client/components/sidebar/SidebarItemContent"
import SidebarItemTooltip from "@/v2-backoffice-client/components/sidebar/SidebarItemTooltip"
import {
  getSidebarItemClasses,
  type SidebarItemVisualProps,
} from "@/v2-backoffice-client/components/sidebar/sidebar.item.constants"

export type SidebarItemProps = Omit<ComponentProps<"button">, "type"> &
  SidebarItemVisualProps

/**
 * A sidebar entry that *does* something in place - toggles a popout, fires an
 * action. Entries that go somewhere are links: use SidebarLink, so the
 * browser gives them middle-click, open-in-new-tab and copy-link for free.
 */
function SidebarItem(props: SidebarItemProps, ref: Ref<HTMLButtonElement>) {
  const {
    className,
    isActive,
    isDisabled,
    isLoading,
    iconSrc,
    iconClassName,
    tooltipContent,
    tooltipDisabled = false,
    isChildrenHiddenOnMini,
    badge,
    children,
    ...buttonProps
  } = props

  const { isMini } = useSidebarContext()

  return (
    <SidebarItemTooltip
      isEnabled={(isMini && !tooltipDisabled) || Boolean(tooltipContent)}
      content={tooltipContent || children}
    >
      <button
        ref={ref}
        type="button"
        disabled={isDisabled}
        className={getSidebarItemClasses({
          isMini,
          isActive,
          isDisabled,
          className,
        })}
        {...buttonProps}
      >
        <SidebarItemContent
          isLoading={isLoading}
          isChildrenHiddenOnMini={isChildrenHiddenOnMini}
          iconSrc={iconSrc}
          iconClassName={iconClassName}
          badge={badge}
        >
          {children}
        </SidebarItemContent>
      </button>
    </SidebarItemTooltip>
  )
}

export default forwardRef(SidebarItem)
